import { toViewer, Viewer } from '@entities/User/permissions';
import { useState } from 'react';
import { useCurrentUser } from './useAuth';

const sameViewer = (a: Viewer | undefined, b: Viewer | undefined): boolean =>
  a === b ||
  (a !== undefined &&
    b !== undefined &&
    a.userId === b.userId &&
    a.memberId === b.memberId &&
    a.admin === b.admin &&
    a.superuser === b.superuser);

/**
 * The current user as a `Viewer`, to hand to the rules of `@entities/User/permissions`.
 *
 * Its identity changes only when one of the viewer's own fields does, not with
 * the `/users/me` response. TanStack's structural sharing already keeps `data`
 * when a refetch answers the same payload, but any other change (a new
 * `updatedAt`, a renamed entity) makes a new object. Callers use the viewer as
 * a memo dependency (the user form derives its rules, schema and loaded values
 * from it), so it must not change with those.
 */
export const useViewer = (): Viewer | undefined => {
  const { data: currentUser } = useCurrentUser();
  const next = toViewer(currentUser);
  // The previous viewer, kept while equal: React's "information from previous renders" pattern.
  const [kept, setKept] = useState(next);

  if (!sameViewer(kept, next)) {
    setKept(next);
    return next;
  }
  return kept;
};
